import { Injectable } from '@angular/core';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { EFindingType } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { ICtaAnalyticsApi } from '@interfaces/campaign-analytics-api.interface';
import {
  IAudioTrackingAnalytics,
  ICaptionAnalytics,
  IChatBotAnalytics,
  IChatBotMultiAnalytics,
  IContentAnalytics,
  ICustomAnalytics,
  IDescriptionAnalytics,
  IGamePlayAnalytics,
  IHashTagAnalytics,
  IMentionAnalytics,
  IMessageAnalytics,
  IProductPlacementAnalytics,
  IShowcaseAnalytics,
  ITalkingPointAiAnalytics,
  ITalkingPointAnalytics,
  ITextTrackingAnalytics,
  ITitleAnalytics,
  IVisualAnalytics,
} from '@interfaces/deliverable-analytics.interface';

const KEYWORD_FINDING_DELIVERABLE_TYPES = [
  EDeliverableType.title,
  EDeliverableType.audioTracking,
  EDeliverableType.textTracking,
  EDeliverableType.talkingPoint,
  EDeliverableType.description,
  EDeliverableType.caption,
  EDeliverableType.hashTag,
  EDeliverableType.mention,
  EDeliverableType.message,
] as const satisfies readonly EDeliverableType[];

const DIRECT_FINDING_DELIVERABLE_TYPES = [
  EDeliverableType.chatBot,
  EDeliverableType.chatbotMulti,
  EDeliverableType.gamePlay,
  EDeliverableType.visual,
  EDeliverableType.showcase,
  EDeliverableType.productPlacement,
  EDeliverableType.content,
  EDeliverableType.talkingPointAi,
  EDeliverableType.custom,
] as const satisfies readonly EDeliverableType[];

type TKeywordFindingDeliverableType = (typeof KEYWORD_FINDING_DELIVERABLE_TYPES)[number];
type TDirectFindingDeliverableType = (typeof DIRECT_FINDING_DELIVERABLE_TYPES)[number];
type TFindingDeliverableType = TKeywordFindingDeliverableType | TDirectFindingDeliverableType;
type TAssertNever<T extends never> = T;
const DELIVERABLE_FINDING_TYPE_COVERAGE_CHECK: [
  TAssertNever<Exclude<EDeliverableType, TFindingDeliverableType>>,
  TAssertNever<Extract<TKeywordFindingDeliverableType, TDirectFindingDeliverableType>>,
] = [] as never;
void DELIVERABLE_FINDING_TYPE_COVERAGE_CHECK;

@Injectable({
  providedIn: 'root',
})
export class DeliverableFindingsService {
  public mapDeliverableTypeToFindingType(deliverableType: EDeliverableType): EFindingType | null {
    const mapping: Record<EDeliverableType, EFindingType> = {
      [EDeliverableType.title]: EFindingType.title,
      [EDeliverableType.gamePlay]: EFindingType.gamePlay,
      [EDeliverableType.chatBot]: EFindingType.chatBot,
      [EDeliverableType.chatbotMulti]: EFindingType.chatbotMulti,
      [EDeliverableType.talkingPoint]: EFindingType.topic,
      [EDeliverableType.visual]: EFindingType.visual,
      [EDeliverableType.productPlacement]: EFindingType.productPlacement,
      [EDeliverableType.showcase]: EFindingType.showcase,
      [EDeliverableType.textTracking]: EFindingType.textTracking,
      [EDeliverableType.audioTracking]: EFindingType.audioTracking,
      [EDeliverableType.caption]: EFindingType.caption,
      [EDeliverableType.hashTag]: EFindingType.hashtag,
      [EDeliverableType.mention]: EFindingType.mention,
      [EDeliverableType.message]: EFindingType.message,
      [EDeliverableType.description]: EFindingType.description,
      [EDeliverableType.content]: EFindingType.content,
      [EDeliverableType.talkingPointAi]: EFindingType.talkingPointAi,
      [EDeliverableType.custom]: EFindingType.custom,
    };

    return mapping[deliverableType] ?? null;
  }

  public mergeFindingsIntoAnalytics(
    analytics: ICtaAnalyticsData,
    findings: unknown[],
    deliverableId: string,
    deliverableType: EDeliverableType,
  ): ICtaAnalyticsData {
    if (!analytics.ctaAnalytics || !deliverableId) {
      return analytics;
    }

    const ctaAnalytics = { ...analytics.ctaAnalytics };

    if (this.isKeywordFindingDeliverableType(deliverableType)) {
      this.processKeywordFindings(deliverableType, ctaAnalytics, deliverableId, findings);
    }

    if (this.isDirectFindingDeliverableType(deliverableType)) {
      this.processDirectFindings(deliverableType, ctaAnalytics, deliverableId, findings);
    }

    return {
      ...analytics,
      ctaAnalytics,
    };
  }

  private processKeywordFindings(
    deliverableType: TKeywordFindingDeliverableType,
    ctaAnalytics: ICtaAnalyticsApi,
    deliverableId: string,
    findings: unknown[],
  ): void {
    switch (deliverableType) {
      case EDeliverableType.title:
        this.mergeKeywordFindings(
          ctaAnalytics.titles,
          deliverableId,
          findings as ITitleAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.audioTracking:
        this.mergeKeywordFindings(
          ctaAnalytics.audioTrackings,
          deliverableId,
          findings as IAudioTrackingAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.textTracking:
        this.mergeKeywordFindings(
          ctaAnalytics.textTrackings,
          deliverableId,
          findings as ITextTrackingAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.talkingPoint:
        this.mergeKeywordFindings(
          ctaAnalytics.talkingPoints,
          deliverableId,
          findings as ITalkingPointAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.description:
        this.mergeKeywordFindings(
          ctaAnalytics.descriptions,
          deliverableId,
          findings as IDescriptionAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.caption:
        this.mergeKeywordFindings(
          ctaAnalytics.captions,
          deliverableId,
          findings as ICaptionAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.hashTag:
        this.mergeKeywordFindings(
          ctaAnalytics.hashtags,
          deliverableId,
          findings as IHashTagAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.mention:
        this.mergeKeywordFindings(
          ctaAnalytics.mentions,
          deliverableId,
          findings as IMentionAnalytics['keywords'][0]['findings'],
        );
        break;
      case EDeliverableType.message:
        this.mergeKeywordFindings(
          ctaAnalytics.messages,
          deliverableId,
          findings as IMessageAnalytics['keywords'][0]['findings'],
        );
        break;
      default:
        this.assertUnhandledDeliverableType(deliverableType);
    }
  }

  private processDirectFindings(
    deliverableType: TDirectFindingDeliverableType,
    ctaAnalytics: ICtaAnalyticsApi,
    deliverableId: string,
    findings: unknown[],
  ): void {
    switch (deliverableType) {
      case EDeliverableType.chatBot:
        this.mergeDirectFindings(ctaAnalytics.chatbot, deliverableId, findings as IChatBotAnalytics['findings']);
        break;
      case EDeliverableType.chatbotMulti:
        this.mergeDirectFindings(
          ctaAnalytics.chatbotMultis,
          deliverableId,
          findings as IChatBotMultiAnalytics['findings'],
        );
        break;
      case EDeliverableType.gamePlay:
        this.mergeDirectFindings(ctaAnalytics.gamePlay, deliverableId, findings as IGamePlayAnalytics['findings']);
        break;
      case EDeliverableType.visual:
        this.mergeDirectFindings(ctaAnalytics.visuals, deliverableId, findings as IVisualAnalytics['findings']);
        break;
      case EDeliverableType.showcase:
        this.mergeDirectFindings(ctaAnalytics.showcases, deliverableId, findings as IShowcaseAnalytics['findings']);
        break;
      case EDeliverableType.productPlacement:
        this.mergeDirectFindings(
          ctaAnalytics.productPlacements,
          deliverableId,
          findings as IProductPlacementAnalytics['findings'],
        );
        break;
      case EDeliverableType.content:
        this.mergeDirectFindings(ctaAnalytics.contents, deliverableId, findings as IContentAnalytics['findings']);
        break;
      case EDeliverableType.talkingPointAi:
        this.mergeDirectFindings(
          ctaAnalytics.talkingPointAis,
          deliverableId,
          findings as ITalkingPointAiAnalytics['findings'],
        );
        break;
      case EDeliverableType.custom:
        this.mergeDirectFindings(ctaAnalytics.customs, deliverableId, findings as ICustomAnalytics['findings']);
        break;
      default:
        this.assertUnhandledDeliverableType(deliverableType);
    }
  }

  private mergeKeywordFindings<
    T extends { deliverableId: string; keywords?: Array<{ keywordId: string; findings?: unknown[] }> },
  >(deliverables: T[], deliverableId: string, findings: Array<{ parentId: string }>): void {
    const index = deliverables.findIndex((d) => d.deliverableId === deliverableId);

    if (index < 0 || !findings.length) {
      return;
    }

    const deliverable = { ...deliverables[index] };

    if (deliverable.keywords) {
      deliverable.keywords = deliverable.keywords.map((keyword) => {
        const keywordFindings = findings.filter((f) => f.parentId === keyword.keywordId);

        return {
          ...keyword,
          findings: keywordFindings,
          findingsCount: keywordFindings.length,
        };
      });
    }

    deliverables[index] = deliverable;
  }

  private mergeDirectFindings<T extends { deliverableId: string; findings?: unknown[]; findingsCount?: number }>(
    deliverables: T[],
    deliverableId: string,
    findings: T['findings'],
  ): void {
    const index = deliverables.findIndex((d) => d.deliverableId === deliverableId);

    if (index < 0) {
      return;
    }

    const deliverable = { ...deliverables[index] } as T & { findingsCount: number };
    deliverable.findings = findings;
    deliverable.findingsCount = findings?.length ?? 0;
    deliverables[index] = deliverable;
  }

  private isKeywordFindingDeliverableType(
    deliverableType: EDeliverableType,
  ): deliverableType is TKeywordFindingDeliverableType {
    return KEYWORD_FINDING_DELIVERABLE_TYPES.includes(deliverableType as TKeywordFindingDeliverableType);
  }

  private isDirectFindingDeliverableType(
    deliverableType: EDeliverableType,
  ): deliverableType is TDirectFindingDeliverableType {
    return DIRECT_FINDING_DELIVERABLE_TYPES.includes(deliverableType as TDirectFindingDeliverableType);
  }

  private assertUnhandledDeliverableType(deliverableType: never): never {
    throw new Error(`Unhandled deliverable type: ${deliverableType}`);
  }
}
